import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { useAuth } from "../context/AuthContext";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Activity, AlertTriangle, CheckCircle2, Clock, Database, RefreshCcw,
  ShieldAlert, Server, Wrench, ChartBar, Timer, Download,
} from "lucide-react";
import TopNav from "../components/TopNav";
import CountryBlocklistManager from "../components/admin/CountryBlocklistManager";
import { toast } from "sonner";

const fmtSeconds = (s) => {
  if (s == null || isNaN(s)) return "—";
  s = Math.max(0, Math.round(s));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const r = s % 60;
  if (m < 60) return `${m}m ${r}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
};

const fmtAgo = (iso) => {
  if (!iso) return "never";
  try {
    const ts = Date.parse(iso);
    const diff = Math.floor((Date.now() - ts) / 1000);
    return diff <= 0 ? "just now" : `${fmtSeconds(diff)} ago`;
  } catch {
    return "—";
  }
};

const StatTile = ({ label, value, sub, Icon, tone = "text-foreground", testid }) => (
  <div className="nd-card p-5" data-testid={testid}>
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <p className="eyebrow">{label}</p>
        <p className={`mt-2 font-display font-bold text-2xl sm:text-3xl font-tabular truncate ${tone}`}>
          {value}
        </p>
        {sub && <p className="mt-1 text-xs text-muted-foreground truncate">{sub}</p>}
      </div>
      <div className="h-10 w-10 rounded-xl bg-muted flex items-center justify-center shrink-0">
        <Icon className="w-4.5 h-4.5 text-foreground" />
      </div>
    </div>
  </div>
);

const HealActionsChart = ({ buckets }) => {
  if (!buckets || !buckets.length) {
    return (
      <div
        className="text-sm text-muted-foreground py-8 text-center"
        data-testid="heal-chart-empty"
      >
        No watchdog actions in the last 24 hours — system has been healthy.
      </div>
    );
  }
  const max = Math.max(1, ...buckets.map((b) => b.total_actions || 0));
  return (
    <div className="flex items-end gap-1 h-32" data-testid="heal-chart">
      {buckets.map((b, i) => {
        const h = Math.max(2, ((b.total_actions || 0) / max) * 100);
        const hr = b.hour_utc?.slice(11, 13);
        const tone = b.total_actions > 0 ? "bg-warning" : "bg-muted";
        return (
          <div
            key={i}
            className="flex-1 flex flex-col items-center justify-end gap-1"
            title={`${b.hour_utc?.slice(0, 16)}Z — ${b.total_actions} action(s)`}
          >
            <div
              className={`w-full ${tone} rounded-t transition-all`}
              style={{ height: `${h}%` }}
              data-testid={`heal-bar-${i}`}
            />
            {(i % 4 === 0 || i === buckets.length - 1) && (
              <span className="text-[10px] text-muted-foreground font-tabular">{hr}</span>
            )}
          </div>
        );
      })}
    </div>
  );
};

const KindBadges = ({ map }) => {
  const entries = Object.entries(map || {});
  if (!entries.length) {
    return <span className="text-xs text-muted-foreground">none</span>;
  }
  return (
    <div className="flex flex-wrap gap-1.5">
      {entries.map(([k, n]) => (
        <Badge key={k} variant="outline" className="text-xs" data-testid={`heal-kind-${k}`}>
          {k.replace(/_/g, " ")} · {n}
        </Badge>
      ))}
    </div>
  );
};

const AdminHealthPage = () => {
  const { user, loading: authLoading, token } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const fetchHealth = useCallback(async () => {
    if (!token) return;
    setRefreshing(true);
    try {
      const { data } = await axios.get(`${API}/admin/health`);
      setData(data);
      setError(null);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || e.message;
      setError({ status, detail });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [token]);

  const syncFixtures = useCallback(async () => {
    if (syncing) return;
    setSyncing(true);
    try {
      const { data } = await axios.post(`${API}/admin/fixtures/sync`);
      toast.success(
        `Sync OK · ${data.sync_result?.total_inserted ?? 0} fixtures inserted` +
          ` · today=${data.fixtures_today}, tomorrow=${data.fixtures_tomorrow}` +
          ` · cleared ${data.prediction_cache_cleared} cache row(s)`,
        { duration: 6000 }
      );
      fetchHealth();
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message;
      toast.error(`Fixtures sync failed: ${String(detail).slice(0, 200)}`, {
        duration: 8000,
      });
    } finally {
      setSyncing(false);
    }
  }, [syncing, fetchHealth]);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate("/auth?redirect=/admin/health", { replace: true });
      return;
    }
    fetchHealth();
    const t = setInterval(fetchHealth, 30000);
    return () => clearInterval(t);
  }, [authLoading, user, navigate, fetchHealth]);

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background">
        <TopNav />
        <div className="container mx-auto px-4 py-12 text-center text-muted-foreground" data-testid="admin-loading">
          Loading admin health…
        </div>
      </div>
    );
  }

  if (error?.status === 403 || (user && !user.is_admin)) {
    return (
      <div className="min-h-screen bg-background">
        <TopNav />
        <div className="container mx-auto px-4 py-12 max-w-xl">
          <div className="nd-card p-8 text-center" data-testid="admin-forbidden">
            <ShieldAlert className="w-10 h-10 text-destructive mx-auto mb-3" />
            <h1 className="font-display font-bold text-2xl mb-2">Admin access required</h1>
            <p className="text-sm text-muted-foreground mb-6">
              Your account doesn't have admin privileges. Contact an administrator
              to add your email to <code className="px-1 py-0.5 rounded bg-muted">ADMIN_EMAILS</code>.
            </p>
            <Button onClick={() => navigate("/")} data-testid="admin-back-home">Back home</Button>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <TopNav />
        <div className="container mx-auto px-4 py-12 max-w-xl">
          <div className="nd-card p-6" data-testid="admin-error">
            <AlertTriangle className="w-6 h-6 text-destructive mb-2" />
            <p className="font-medium">Failed to load admin health</p>
            <p className="text-sm text-muted-foreground mt-1">
              {String(error.detail)} (status: {error.status || "—"})
            </p>
            <Button className="mt-4" onClick={fetchHealth} data-testid="admin-retry">Retry</Button>
          </div>
        </div>
      </div>
    );
  }

  const wd = data?.watchdog || {};
  const cron = data?.cron || { jobs: [] };
  const betway = data?.betway_availability || {};
  const cache = data?.prediction_cache || {};
  const settle = data?.last_settle || {};
  const heals = data?.heal_history_24h || { buckets: [], totals_by_kind: {}, tick_count: 0 };

  const stuckTone = cache.stuck_rows > 0 ? "text-destructive" : "text-success";
  const betwayAge = betway.age_s;
  const betwayTone = betwayAge == null
    ? "text-muted-foreground"
    : betwayAge < 600 ? "text-success" : betwayAge < 1800 ? "text-warning" : "text-destructive";

  return (
    <div className="min-h-screen bg-background" data-testid="admin-health-page">
      <TopNav />
      <div className="container mx-auto px-4 py-8 max-w-7xl">
        {/* Header */}
        <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
          <div>
            <p className="eyebrow flex items-center gap-2">
              <Server className="w-3.5 h-3.5" /> System health
            </p>
            <h1 className="font-display font-bold text-3xl sm:text-4xl mt-1">
              Admin / Health
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Live diagnostics — auto-refreshes every 30 seconds
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button
              variant="outline"
              onClick={() => navigate("/admin/data-gaps")}
              data-testid="admin-data-gaps-link"
            >
              <Database className="w-4 h-4 mr-2" />
              Data gaps
            </Button>
            <Button
              onClick={syncFixtures}
              disabled={syncing}
              data-testid="admin-sync-fixtures"
            >
              <Download className={`w-4 h-4 mr-2 ${syncing ? "animate-pulse" : ""}`} />
              {syncing ? "Syncing…" : "Sync fixtures now"}
            </Button>
            <Button
              variant="outline"
              onClick={fetchHealth}
              disabled={refreshing}
              data-testid="admin-refresh"
            >
              <RefreshCcw className={`w-4 h-4 mr-2 ${refreshing ? "animate-spin" : ""}`} />
              Refresh
            </Button>
          </div>
        </div>

        {/* Top stat tiles */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <StatTile
            label="Watchdog last run"
            value={fmtAgo(wd.last_run_at)}
            sub={`${wd.actions_total || 0} total auto-heals`}
            Icon={Wrench}
            tone={wd.last_run_at ? "text-success" : "text-muted-foreground"}
            testid="tile-watchdog"
          />
          <StatTile
            label="Last settle"
            value={fmtAgo(settle.ran_at)}
            sub={
              settle.result?.markets_settled_this_run != null
                ? `${settle.result.markets_settled_this_run} markets graded`
                : "no recent run"
            }
            Icon={CheckCircle2}
            tone={settle.ran_at ? "text-success" : "text-warning"}
            testid="tile-settle"
          />
          <StatTile
            label="Betway cache age"
            value={fmtSeconds(betwayAge)}
            sub={`${Object.keys(betway.leagues || {}).length} leagues${betway.refreshing ? " · refreshing…" : ""}`}
            Icon={Database}
            tone={betwayTone}
            testid="tile-betway"
          />
          <StatTile
            label="Stuck cache rows"
            value={String(cache.stuck_rows ?? 0)}
            sub={`${cache.total_rows ?? 0} total · ${cache.compiling_rows ?? 0} compiling`}
            Icon={cache.stuck_rows > 0 ? AlertTriangle : CheckCircle2}
            tone={stuckTone}
            testid="tile-stuck"
          />
        </div>

        {/* Watchdog details */}
        <section className="nd-card p-5 mb-6" data-testid="section-watchdog">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Wrench className="w-4 h-4 text-foreground" />
              <h2 className="font-display font-bold text-lg">Auto-repair watchdog</h2>
            </div>
            <Badge variant={wd.running ? "default" : "secondary"} data-testid="watchdog-status">
              {wd.running ? "running" : "idle"}
            </Badge>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div>
              <p className="eyebrow mb-1">Last run</p>
              <p className="font-medium" data-testid="watchdog-last-run">
                {wd.last_run_at ? new Date(wd.last_run_at).toUTCString() : "Not yet run"}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Cadence: every {wd.interval_s ?? 60}s
              </p>
            </div>
            <div>
              <p className="eyebrow mb-1">Last actions</p>
              <KindBadges map={wd.actions_last_run} />
            </div>
            <div>
              <p className="eyebrow mb-1">Lifetime totals</p>
              <p className="font-display font-bold text-2xl font-tabular" data-testid="watchdog-total">
                {wd.actions_total ?? 0}
              </p>
            </div>
          </div>
        </section>

        {/* 24h heal history */}
        <section className="nd-card p-5 mb-6" data-testid="section-heal-history">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <ChartBar className="w-4 h-4 text-foreground" />
              <h2 className="font-display font-bold text-lg">24h heal-actions</h2>
            </div>
            <p className="text-xs text-muted-foreground">
              {heals.tick_count} healing tick(s) recorded
            </p>
          </div>
          <HealActionsChart buckets={heals.buckets} />
          <div className="mt-3">
            <p className="eyebrow mb-1.5">By kind (24h)</p>
            <KindBadges map={heals.totals_by_kind} />
          </div>
        </section>

        {/* Cron jobs */}
        <section className="nd-card p-5 mb-6" data-testid="section-crons">
          <div className="flex items-center gap-2 mb-4">
            <Timer className="w-4 h-4 text-foreground" />
            <h2 className="font-display font-bold text-lg">Cron jobs · {cron.jobs?.length || 0}</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="cron-table">
              <thead className="text-left text-muted-foreground">
                <tr className="border-b border-border">
                  <th className="py-2 pr-3 font-medium">Name</th>
                  <th className="py-2 pr-3 font-medium">Schedule</th>
                  <th className="py-2 pr-3 font-medium">Next run</th>
                  <th className="py-2 pr-3 font-medium">Last run</th>
                  <th className="py-2 pr-3 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {(cron.jobs || []).map((j) => (
                  <tr key={j.name} className="border-b border-border/40" data-testid={`cron-row-${j.name}`}>
                    <td className="py-2 pr-3 font-medium">{j.name}</td>
                    <td className="py-2 pr-3 text-muted-foreground">{j.schedule_utc}</td>
                    <td className="py-2 pr-3 font-tabular">
                      in {fmtSeconds(j.seconds_until_next_run)}
                    </td>
                    <td className="py-2 pr-3 text-muted-foreground">{fmtAgo(j.last_run_at)}</td>
                    <td className="py-2 pr-3">
                      {j.last_error ? (
                        <Badge variant="destructive" className="text-xs">error</Badge>
                      ) : j.running ? (
                        <Badge className="text-xs">running</Badge>
                      ) : j.last_run_at ? (
                        <Badge variant="outline" className="text-xs">ok</Badge>
                      ) : (
                        <Badge variant="secondary" className="text-xs">pending</Badge>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Prediction cache */}
        <section className="nd-card p-5 mb-6" data-testid="section-cache">
          <div className="flex items-center gap-2 mb-4">
            <Database className="w-4 h-4 text-foreground" />
            <h2 className="font-display font-bold text-lg">Prediction cache</h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4 text-sm">
            <div>
              <p className="eyebrow">Total rows</p>
              <p className="font-display font-bold text-2xl font-tabular">{cache.total_rows ?? 0}</p>
            </div>
            <div>
              <p className="eyebrow">Compiling</p>
              <p className="font-display font-bold text-2xl font-tabular">{cache.compiling_rows ?? 0}</p>
            </div>
            <div>
              <p className="eyebrow">Stuck</p>
              <p className={`font-display font-bold text-2xl font-tabular ${stuckTone}`}>
                {cache.stuck_rows ?? 0}
              </p>
            </div>
            <div>
              <p className="eyebrow">Newest compiled</p>
              <p className="font-medium">{fmtAgo(cache.newest_compiled_at)}</p>
            </div>
          </div>
          {cache.rows && cache.rows.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-muted-foreground">
                  <tr className="border-b border-border">
                    <th className="py-2 pr-3 font-medium">Cache key</th>
                    <th className="py-2 pr-3 font-medium">Predictions</th>
                    <th className="py-2 pr-3 font-medium">Compiled</th>
                    <th className="py-2 pr-3 font-medium">State</th>
                  </tr>
                </thead>
                <tbody>
                  {cache.rows.map((r) => (
                    <tr key={r.cache_key} className="border-b border-border/40">
                      <td className="py-2 pr-3 font-tabular text-xs">{r.cache_key}</td>
                      <td className="py-2 pr-3 font-tabular">{r.prediction_count}</td>
                      <td className="py-2 pr-3 text-muted-foreground">{fmtAgo(r.compiled_at)}</td>
                      <td className="py-2 pr-3">
                        {r.compiling ? (
                          <Badge className="text-xs">compiling</Badge>
                        ) : (
                          <Badge variant="outline" className="text-xs">idle</Badge>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* Betway availability cache */}
        <section className="nd-card p-5 mb-6" data-testid="section-betway">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Activity className="w-4 h-4 text-foreground" />
              <h2 className="font-display font-bold text-lg">Betway availability cache</h2>
            </div>
            <div className="flex gap-2">
              {betway.refreshing && <Badge>refreshing</Badge>}
              {betway.stale_serving && <Badge variant="secondary">stale-serving</Badge>}
              {betway.last_error && <Badge variant="destructive">error</Badge>}
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm">
            <div>
              <p className="eyebrow">Cache age</p>
              <p className={`font-display font-bold text-2xl font-tabular ${betwayTone}`}>
                {fmtSeconds(betwayAge)}
              </p>
            </div>
            <div>
              <p className="eyebrow">Leagues cached</p>
              <p className="font-display font-bold text-2xl font-tabular">
                {Object.keys(betway.leagues || {}).length}
              </p>
            </div>
            <div>
              <p className="eyebrow">Total fixtures</p>
              <p className="font-display font-bold text-2xl font-tabular">
                {Object.values(betway.leagues || {}).reduce((a, b) => a + (b || 0), 0)}
              </p>
            </div>
          </div>
          {betway.last_error && (
            <p className="mt-3 text-xs text-destructive font-mono">{betway.last_error}</p>
          )}
        </section>

        {/* Iter 145w — Admin-editable country blocklist for daily slips. */}
        <div className="mt-6">
          <CountryBlocklistManager />
        </div>

        <p className="text-xs text-muted-foreground text-center mt-8 mb-4 flex items-center justify-center gap-1.5">
          <Clock className="w-3 h-3" />
          Snapshot at {data?.now_utc ? new Date(data.now_utc).toUTCString() : "—"}
        </p>
      </div>
    </div>
  );
};

export default AdminHealthPage;
